<template>
  <div class="ServicePage">
    <div class="service-banner">
      <div class="box">
        <div class="banner-content center">
          <h1>{{ bannerData.title }}</h1>
          <p>{{ bannerData.subtitle }}</p>
          <div class="banner-tagline">
            {{ bannerData.tagline }}
          </div>
          <div class="banner-features">
            <span v-for="(feature, index) in bannerData.features" :key="index" style="white-space: pre-line;">
              <strong>{{ feature.strong_text }}</strong>
              {{ feature.normal_text }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="service-tabs-container Pt75">
      <div class="box">
        <ul class="service-tabs">
          <li
              v-for="tab in serviceTabs"
              :key="tab.key"
              :class="{ active: activeTab === tab.key }"
              @click="activeTab = tab.key"
          >
            {{ tab.name }}
          </li>
        </ul>

        <div class="service-content Pt75">
          <div v-if="activeTab === 'faq'" class="faq-section">
            <div class="faq-categories">
              <button
                  v-for="category in faqCategories"
                  :key="category.id"
                  :class="{ active: activeFaqCategory && activeFaqCategory.id === category.id }"
                  @click="activeFaqCategory = category"
              >
                {{ category.name }}
              </button>
            </div>
            <div class="faq-list">
              <div v-for="item in filteredFaqs" :key="item.id" class="faq-item">
                <div class="faq-question" @click="toggleFaq(item.id)">
                  <span>{{ item.question }}<template v-if="!item.question.endsWith('？')">&nbsp;&nbsp;</template></span>
                  <span :class="['arrow', { open: item.isOpen }]"></span>
                </div>
                <div v-if="item.isOpen" class="faq-answer" v-html="item.answer"></div>
              </div>
              <p v-if="!filteredFaqs.length" class="no-results">该分类下暂无常见问题。</p>
            </div>
          </div>

          <div v-if="activeTab === 'booking'" class="booking-section">
            <div class="booking-card">
              <img src="https://via.placeholder.com/100x100.png?text=Online+Icon" alt="在线客服">
              <h3>在线客服</h3>
              <p>随时随地，方便快捷</p>
              <p>在线预约，无需等待</p>
              <button class="primary-btn">立即咨询</button>
            </div>
            <div class="booking-card">
              <img src="https://via.placeholder.com/100x100.png?text=Phone+Icon" alt="服务热线">
              <h3>400-900-9199</h3>
              <p>全国统一服务热线</p>
              <p>周一至周六 08:30 — 18:00</p>
              <button class="secondary-btn">拨打电话</button>
            </div>
          </div>

          <div v-if="activeTab === 'outline'" class="outline-section">
            <div class="service-hotline-block">
              <div class="hotline-icon-container">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="80" height="80">
                  <path
                      d="M12 2a10 10 0 00-4.08 19.34l-1.39 1.39a1 1 0 000 1.41 1 1 0 001.41 0l1.39-1.39A9.91 9.91 0 0012 22a10 10 0 000-20zm0 18a8 8 0 118-8 8 8 0 01-8 8z"/>
                  <path
                      d="M19.1 8.83l-1.06-1.06-2.58 2.58-.71-.71 2.58-2.58-1.06-1.06a4.28 4.28 0 00-6 0l-4.24 4.24a4.28 4.28 0 000 6l1.06 1.06 2.58-2.58.71.71-2.58 2.58 1.06 1.06a4.28 4.28 0 006 0l4.24-4.24a4.28 4.28 0 000-6zm-1.41 4.24l-4.24 4.24a2.28 2.28 0 01-3.18 0l-1.06-1.06a2.28 2.28 0 010-3.18L13.46 9l.71.71L10 13.88l-1.06-1.06a.28.28 0 00-.39 0l-.71.71a.28.28 0 000 .39L9 15l2.12 2.12.71-.71L13 15.29l-3.54 3.54a2.29 2.29 0 013.18 0l1.06 1.06a2.29 2.29 0 010 3.18l-1.77 1.77-.71-.71 1.06-1.06a.28.28 0 000-.39l-.71-.71a.28.28 0 00-.39 0L17 15l-2.12-2.12-.71.71L15.29 14l-3.54 3.54a2.27 2.27 0 01-3.18 0l-1.06-1.06a2.27 2.27 0 010-3.18l.71-.71h.01z"/>
                </svg>
              </div>
              <div class="hotline-text-container">
                <h2>PARKCO百奥</h2>
                <p>全国1300家百奥专业服务站</p>
                <p>提供安心的售后服务</p>
                <span>AFTER-SALES SERVICE</span>
              </div>
              <div class="hotline-contact-container">
                <p class="hotline-title">售后服务热线:</p>
                <p class="hotline-number">400-900-9199 <span class="hotline-ext">转 2</span></p>
                <p class="hotline-desc">
                  您在使用过程中遇到任何疑问，欢迎拨打PARKCO百奥全国服务热线，我们的工作人员会给您满意的答复</p>
              </div>
            </div>
          </div>

          <div v-if="activeTab === 'policy'" class="policy-section">
            <h2>产品保修说明与退换管理规定</h2>
            <div v-for="section in servicePolicy" :key="section.title" class="policy-content-section">
              <h3>{{ section.title }}</h3>
              <ul>
                <li v-for="(clause, index) in section.clauses.split('\n')" :key="index">
                  {{ clause }}
                </li>
              </ul>
            </div>
          </div>

          <div v-if="activeTab === 'outlets'" class="outlets-section">
            <p class="outlets-intro">
              全国售后服务网点近450余个，以广州总部为核心辐射全国各地，距您最近的售后网点请致电：<strong>400 900
              9199转2</strong>咨询。</p>
            <div class="outlets-layout">
              <div class="region-list">
                <span v-for="region in regions" :key="region">{{ region }}</span>
              </div>
              <div class="map-placeholder">
                <img src="https://via.placeholder.com/600x400.png?text=中国地图区域划分" alt="中国地图售后网点"></div>
            </div>
          </div>

          <div v-if="activeTab === 'complaints'" class="complaints-section">
            <h3>尊敬的客户：</h3>
            <p>感谢您对Waykar威卡的信任与支持！</p>
            <p>您的每一次反馈，都是我们进步的动力；</p>
            <p>您的每一条建议，都是我们提升的方向。</p>
            <p>
              我们珍视您的意见，无论是产品体验还是服务细节，您的反馈将帮助我们不断优化，为您提供更卓越的空气管理解决方案。</p>
            <p>如您有任何建议或意见，欢迎通过以下方式联系我们，我们将竭诚为您服务：</p>
            <p>邮箱： <a style="color: #47975a " href="mailto:support@waykar.com">
              support@waykar.com
            </a></p>
            <p>电话： <span style="color: #47975a ">
              0755-2108 5376
            </span></p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {getPageHeader, getServiceFaqCategory, getServiceFaq, getServicePolicy, getSetting} from '@/api/request';

export default {
  name: 'ServicePage',
  data() {
    return {
      activeTab: 'faq', // Default active tab
      serviceTabs: [
        {key: 'faq', name: '常见问题'},
        {key: 'complaints', name: '投诉建议'},
      ],
      bannerData: {},
      activeFaqCategory: null,
      faqCategories: [],
      allFaqs: [],
      servicePolicy: [
        {
          title: "三包服务政策",
          clauses: "1.1 凡属百奥系列产品的严格按照国家、本公司相关规定实行最新三包服务政策。\n1.2 用户凭有效发票、保修卡或有效购机证明均可免费享受保修服务。\n1.3 百奥系列产品凡是用于工、商业用途的全部整机保修1年主要部件3年，部分工业机整机保修18个月主要部件3年，家用系列产品整机1到3年、3到5年不等部分定制标准产品则按照销售合同承诺书为准。",
        },
      ],
      regions: ['华东', '东北', '华南', '华北', '华中', '西北', '西南'],
      settings: {
        contactNumber: '',
      }
    };
  },
  computed: {
    filteredFaqs() {
      // 增强容错性，确保数据存在且格式正确
      if (!this.activeFaqCategory || !this.allFaqs || !Array.isArray(this.allFaqs)) {
        return [];
      }

      // 过滤出对应分类的FAQ
      const filtered = this.allFaqs.filter(faq =>
          faq.category_id === this.activeFaqCategory.id
      );

      return filtered;
    },
  },
  watch: {
    // 监听路由变化
    '$route'(to) {
      if (to.name === 'ServicePage' && to.query.category) {
        this.setDefaultCategory(to.query.category);
      }
      // 处理tab参数
      if (to.query.tab) {
        this.activeTab = to.query.tab;
      }
    }
  },
  methods: {
    fetchSettings() {
      getSetting().then(response => {
        const settingsData = response.list;
        this.settings.contactNumber = this.findSetting(settingsData, '联系方式').value_string;
      });
    },
    findSetting(settings, name) {
      return settings.find(setting => setting.name === name) || {};
    },
    toggleFaq(faqId) {
      const faqItem = this.allFaqs.find(faq => faq.id === faqId);
      if (faqItem) {
        faqItem.isOpen = !faqItem.isOpen;
      }
    },
    async fetchPageHeader() {
      try {
        const response = await getPageHeader({banner_key: '售后服务'});
        const bannerData = response.list[0];
        bannerData.features = JSON.parse(bannerData.features);
        this.bannerData = bannerData;
      } catch (error) {
        console.error('Error fetching page header:', error);
      }
    },
    async fetchFaqCategories() {
      try {
        const response = await getServiceFaqCategory();
        this.faqCategories = response.list;
        if (this.faqCategories.length > 0) {
          // 检查URL中是否有category参数，如果有则设置默认分类
          if (this.$route.query.category) {
            this.setDefaultCategory(this.$route.query.category);
          }

          // 如果仍然没有设置活动分类，则默认选择第一个
          if (!this.activeFaqCategory) {
            this.activeFaqCategory = this.faqCategories[0];
          }
        }
        // 总是获取FAQ列表，不依赖于分类选择
        await this.fetchFaqs();
      } catch (error) {
        console.error('Error fetching FAQ categories:', error);
      }
    },
    async fetchFaqs() {
      try {
        const response = await getServiceFaq({ pageSize: 202, page: 1 });
        console.log(response.list)
        this.allFaqs = response.list.map(faq => ({
          ...faq,
          isOpen: false,
          // 确保必要的字段存在
          id: faq.id || Math.random(), // 如果没有id则生成一个临时id
          category_id: faq.category_id || (this.faqCategories.length > 0 ? this.faqCategories[0].id : 0)
        }));

        // 如果当前没有激活的分类但有FAQ分类，则设置第一个为激活分类
        if (!this.activeFaqCategory && this.faqCategories.length > 0) {
          this.activeFaqCategory = this.faqCategories[0];
        }
      } catch (error) {
        console.error('Error fetching FAQs:', error);
      }
    },
    async fetchServicePolicy() {
      try {
        const response = await getServicePolicy();
        this.servicePolicy = response.list;
      } catch (error) {
        console.error('Error fetching service policy:', error);
      }
    },
    // 根据分类ID设置默认分类
    setDefaultCategory(categoryId) {
      const category = this.faqCategories.find(cat => cat.id === parseInt(categoryId));
      if (category) {
        this.activeFaqCategory = category;
      }
    },
  },
  created() {
    this.fetchPageHeader();
    this.fetchFaqCategories();
    this.fetchServicePolicy();
    // 确保FAQ数据加载
    this.fetchFaqs();
    
    // 处理URL中的tab参数
    if (this.$route.query.tab) {
      this.activeTab = this.$route.query.tab;
    }
  },
  mounted() {
    this.fetchSettings();
  },
};
</script>

<style scoped>
/* Base Styles & Utilities */
.Pt75 {
  padding-top: 75px;
  padding-bottom: 75px;
}

.box {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
}

.center {
  text-align: center;
}

/* Service Banner Styles - CORRECTED COLORS */
.service-banner {
  background: #47975a;
  color: white;
  padding: 80px 0;
}

.banner-content h1 {
  font-size: 56px;
  font-weight: bold;
  margin-bottom: 10px;
}

.banner-content > p { /* For "MAINTENANCE SERVICES" */
  font-size: 24px;
  opacity: 0.9;
  margin-bottom: 20px;
  text-transform: uppercase;
  letter-spacing: 2px;
}

.banner-tagline {
  opacity: 0.85;
  max-width: 700px;
  margin: 0 auto 40px;
  line-height: 1.7;
}

.banner-features {
  display: flex;
  justify-content: center;
  gap: 30px;
  flex-wrap: wrap;
}

.banner-features span {
  background: rgba(255, 255, 255, 0.15);
  padding: 15px 25px;
  border-radius: 8px;
  font-size: 16px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.banner-features span strong {
  font-size: 36px;
  display: block;
  margin-bottom: 5px;
  font-weight: 700;
}

/* Tab Navigation Styles - CORRECTED COLORS */
.service-tabs-container {
  background-color: #f8f9fa;
  padding-top: 0;
}

.service-tabs {
  list-style: none;
  padding: 0;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  gap: 5px;
  border-bottom: 1px solid #dee2e6;
  justify-content: center;
}

.service-tabs li {
  padding: 18px 30px;
  font-size: 17px;
  font-weight: 500;
  color: #495057;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  transition: all 0.3s ease;
  margin-bottom: -1px;
}

.service-tabs li:hover {
  color: #47975a; /* Green hover */
  border-bottom-color: #47975a; /* Lighter green border */
}

.service-tabs li.active {
  color: #47975a; /* Green active */
  border-bottom-color: #47975a; /* Green active border */
  font-weight: 600;
}

/* Service Content General */
.service-content {
  background-color: #fff;
  padding-bottom: 75px;
}

/* FAQ Section Styles - CORRECTED COLORS */
.faq-section {
  max-width: 900px;
  margin: 0 auto;
}

.faq-categories {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 40px;
}

.faq-categories button {
  padding: 10px 20px;
  font-size: 16px;
  background-color: #f0f2f5;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.faq-categories button:hover {
  background-color: #e2e8f0;
  border-color: #c9d1da;
}

.faq-categories button.active {
  background-color: #47975a; /* Green active button */
  color: white;
  border-color: #47975a; /* Green active button border */
}

.faq-item {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 15px;
  overflow: hidden;
}

.faq-question {
  padding: 18px 25px;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  cursor: pointer;
  background-color: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.faq-question:hover {
  background-color: #f1f3f5;
}

.faq-question .arrow {
  margin-right: 8px;
  width: 12px;
  height: 12px;
  border-left: 2px solid #555;
  border-bottom: 2px solid #555;
  transform: rotate(-45deg);
  transition: transform 0.3s ease;
}

.faq-question span {
  width: 93%;
  font-size: 16px;
}

.faq-question .arrow.open {
  transform: rotate(135deg);
}

.faq-answer {
  padding: 20px 25px;
  font-size: 16px;
  line-height: 1.7;
  color: #555;
  background-color: #fff;
  border-top: 1px solid #e9ecef;
}

.no-results {
  text-align: center;
  font-size: 16px;
  color: #777;
  margin-top: 30px;
}

/* Booking Section Styles - CORRECTED COLORS */
.booking-section {
  display: flex;
  justify-content: space-around;
  gap: 30px;
  flex-wrap: wrap;
  max-width: 900px;
  margin: 0 auto;
}

.booking-card {
  background-color: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  width: 350px;
  box-shadow: 0 5px 25px rgba(0, 0, 0, 0.07);
}

.booking-card img {
  margin-bottom: 20px;
  height: 80px;
}

.booking-card h3 {
  font-size: 24px;
  font-weight: 600;
  color: #47975a; /* Green heading */
  margin-bottom: 15px;
}

.booking-card p {
  font-size: 16px;
  color: #666;
  margin-bottom: 10px;
}

.booking-card .primary-btn, .booking-card .secondary-btn {
  padding: 12px 30px;
  font-size: 16px;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 500;
  display: inline-block;
  margin-top: 20px;
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;
}

.primary-btn {
  background-color: #47975a; /* Green button */
  color: white;
  border: none;
}

.primary-btn:hover {
  background-color: #3a7c4a; /* Darker green hover */
}

.secondary-btn {
  background-color: transparent;
  color: #47975a; /* Green text/border */
  border: 2px solid #47975a; /* Green border */
}

.secondary-btn:hover {
  background-color: #47975a; /* Green background on hover */
  color: white;
}

/* Outline Section Styles - CORRECTED COLORS */
.service-hotline-block {
  display: flex;
  background-color: #343a40;
  color: white;
  border-radius: 12px;
  padding: 40px;
  gap: 30px;
  align-items: center;
  max-width: 1100px;
  margin: 30px auto;
}

.hotline-icon-container {
  background-color: #47975a; /* Green accent */
  padding: 30px;
  border-radius: 8px;
  align-self: flex-start;
}

.hotline-text-container {
  flex-grow: 1;
}

.hotline-text-container h2 {
  font-size: 28px;
  font-weight: bold;
  margin: 0 0 10px 0;
}

.hotline-text-container p {
  font-size: 16px;
  opacity: 0.9;
  margin: 5px 0;
}

.hotline-text-container span { /* AFTER-SALES SERVICE */
  display: block;
  font-size: 14px;
  color: #47975a; /* Green accent */
  font-weight: bold;
  margin-top: 15px;
  letter-spacing: 1px;
}

.hotline-contact-container {
  text-align: right;
  border-left: 1px solid #495057;
  padding-left: 30px;
}

.hotline-title {
  font-size: 16px;
  opacity: 0.8;
  margin-bottom: 5px;
}

.hotline-number {
  font-size: 32px;
  font-weight: bold;
  color: #47975a; /* Green highlight number */
  margin-bottom: 10px;
}

.hotline-number .hotline-ext {
  font-size: 22px;
  font-weight: normal;
  color: white;
}

.hotline-desc {
  font-size: 14px;
  line-height: 1.6;
  max-width: 300px;
  opacity: 0.8;
}

/* Policy Section Styles - CORRECTED COLORS */
.policy-section {
  max-width: 900px;
  margin: 0 auto;
  line-height: 1.8;
}

.policy-section h2 {
  text-align: center;
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 40px;
}

.policy-content-section h3 {
  font-size: 20px;
  font-weight: 600;
  margin: 30px 0 15px 0;
  color: #47975a; /* Green heading */
}

.policy-content-section ul {
  list-style-type: none;
  padding-left: 0;
}

.policy-content-section li {
  font-size: 16px;
  color: #555;
  margin-bottom: 1em;
  line-height: 1.8;
}

/* Outlets Section Styles - CORRECTED COLORS */
.outlets-section {
  max-width: 1000px;
  margin: 0 auto;
}

.outlets-intro {
  font-size: 16px;
  color: #555;
  text-align: center;
  margin-bottom: 40px;
  line-height: 1.7;
}

.outlets-layout {
  display: flex;
  gap: 30px;
}

.region-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-shrink: 0;
  padding-right: 20px;
  border-right: 1px solid #e9ecef;
}

.region-list span {
  font-size: 18px;
  font-weight: 500;
  color: #333;
  padding: 8px 15px;
  border-radius: 6px;
  background-color: #f8f9fa;
  cursor: pointer;
}

.region-list span:hover {
  background-color: #e9ecef;
  color: #47975a; /* Green hover */
}

.map-placeholder img {
  width: 100%;
  height: auto;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

/* Complaints Section Styles - CORRECTED COLORS */
.complaints-section {
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  line-height: 1.8;
}

.complaints-section h3 {
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 20px;
}

.complaints-section p {
  font-size: 17px;
  color: #555;
  margin-bottom: 1.5em;
  text-align: left;
}

.complaints-contact {
  font-weight: 500;
  font-size: 18px !important;
  text-align: center !important;
  margin-top: 30px !important;
}

.complaints-contact strong {
  color: #47975a; /* Green highlight */
}

/* Responsive adjustments */
@media (max-width: 992px) {
  .banner-features span {
    width: calc(50% - 15px);
  }

  .service-hotline-block {
    flex-direction: column;
    text-align: center;
  }

  .hotline-icon-container {
    align-self: center;
  }

  .hotline-contact-container {
    text-align: center;
    border-left: none;
    padding-left: 0;
    border-top: 1px solid #495057;
    padding-top: 20px;
    margin-top: 20px;
  }

  .outlets-layout {
    flex-direction: column;
  }

  .region-list {
    flex-direction: row;
    flex-wrap: wrap;
    border-right: none;
    border-bottom: 1px solid #e9ecef;
    padding-bottom: 20px;
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .banner-content h1 {
    font-size: 40px;
  }

  .banner-content > p {
    font-size: 20px;
  }

  .banner-features span {
    width: 100%;
  }



  .service-tabs li {
    border-bottom: 1px solid #dee2e6;
    text-align: center;
  }

  .service-tabs li.active {
    border-bottom: 3px solid #47975a;
  }

  .booking-card {
    width: 100%;
  }
}


</style>
